/**
 * 🆕 [Phase 31 #1] EmployeeSelector — Composant global avec recherche + sections actifs/inactifs.
 *
 * Props :
 *  - employees: array (passé par le parent, déjà fetché via /api/employees?include_inactive=true)
 *  - value: string (id sélectionné) ou array (si multiple=true)
 *  - onChange: fn(id | id[])
 *  - multiple?: bool (default false)
 *  - includeInactive?: bool (default true) — si false, masque la section "Inactifs"
 *  - placeholder?: string
 *  - disabled?: bool
 *
 * Adoption progressive : remplace les dropdowns existants un par un. Pour la
 * première vague (Phase 31 #1) : RapportHeures, CreerBon/EditerBon, ApprobationConges.
 */
import { useMemo, useState } from 'react';

export function EmployeeSelector({
  employees = [],
  value,
  onChange,
  multiple = false,
  includeInactive = true,
  placeholder = 'Rechercher un employé…',
  disabled = false,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const selectedIds = useMemo(
    () => (multiple ? (value || []) : (value ? [value] : [])),
    [value, multiple],
  );

  const norm = (s) => (s || '').toString().toLowerCase().trim();
  const matches = (e) => {
    if (!q) return true;
    const needle = norm(q);
    return [e.nom, e.prenom, e.email, `${e.prenom || ''} ${e.nom || ''}`]
      .some(s => norm(s).includes(needle));
  };

  const actifs = employees.filter(e => (e.est_actif !== false) && matches(e));
  const inactifs = includeInactive
    ? employees.filter(e => (e.est_actif === false) && matches(e))
    : [];

  const labelFor = (e) => `${e.prenom || ''} ${e.nom || ''}`.trim() || e.email || e.id;

  const handlePick = (id) => {
    if (multiple) {
      const next = selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id];
      onChange?.(next);
    } else {
      onChange?.(id);
      setOpen(false);
    }
  };

  const selectedLabel = !multiple && value
    ? (employees.find(e => e.id === value) ? labelFor(employees.find(e => e.id === value)) : '')
    : '';

  return (
    <div className="relative" data-testid="employee-selector">
      <input
        type="text"
        value={open ? q : (selectedLabel || q)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-100"
        data-testid="employee-selector-input"
      />
      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded shadow-lg"
          data-testid="employee-selector-dropdown"
        >
          <div className="px-2 py-1 text-[10px] uppercase font-bold text-slate-500 bg-slate-50 sticky top-0">
            Actifs ({actifs.length})
          </div>
          {actifs.length === 0 ? (
            <p className="px-2 py-1 text-xs text-slate-400 italic">Aucun actif trouvé</p>
          ) : actifs.map(e => (
            <button
              type="button"
              key={e.id}
              onClick={() => handlePick(e.id)}
              className={
                'block w-full text-left px-2 py-1.5 text-sm hover:bg-blue-50 ' +
                (selectedIds.includes(e.id) ? 'bg-blue-100 font-medium' : '')
              }
              data-testid={`employee-selector-opt-${e.id}`}
            >
              {labelFor(e)}
              {e.email && <span className="text-[10px] text-slate-400 ml-1">· {e.email}</span>}
            </button>
          ))}
          {includeInactive && (
            <>
              <div className="px-2 py-1 text-[10px] uppercase font-bold text-slate-500 bg-slate-50 border-t border-slate-200 sticky top-0">
                Inactifs ({inactifs.length})
              </div>
              {inactifs.length === 0 ? (
                <p className="px-2 py-1 text-xs text-slate-400 italic">Aucun inactif</p>
              ) : inactifs.map(e => (
                <button
                  type="button"
                  key={e.id}
                  onClick={() => handlePick(e.id)}
                  className={
                    'block w-full text-left px-2 py-1.5 text-sm hover:bg-slate-50 text-slate-500 ' +
                    (selectedIds.includes(e.id) ? 'bg-blue-100 font-medium' : '')
                  }
                  data-testid={`employee-selector-opt-inactif-${e.id}`}
                >
                  {labelFor(e)}
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">
                    Inactif
                  </span>
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="block w-full text-center px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 border-t"
            data-testid="employee-selector-close"
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}

export default EmployeeSelector;
